
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { MessageCircle, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { MessageDialog } from "@/components/services/MessageDialog";
import { Textarea } from "@/components/ui/textarea";

export const MessageList = () => {
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isComposingMessage, setIsComposingMessage] = useState(false);
  const [quickReply, setQuickReply] = useState("");
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string } | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: messages, isLoading } = useQuery({
    queryKey: ["professional-messages"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("messages")
        .select(`
          *,
          sender:profiles!sender_id(full_name)
        `)
        .eq("receiver_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async ({ content, receiverId }: { content: string; receiverId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      
      const { error } = await supabase
        .from("messages")
        .insert({
          content,
          sender_id: user.id,
          receiver_id: receiverId,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      setQuickReply("");
      setIsComposingMessage(false);
      queryClient.invalidateQueries({ queryKey: ["professional-messages"] });
      toast({
        title: "Message envoyé",
        description: "Votre message a été envoyé avec succès",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'envoyer le message",
      });
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await supabase
        .from("messages")
        .delete()
        .eq("id", messageId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["professional-messages"] });
      toast({
        title: "Message supprimé",
        description: "Le message a été supprimé avec succès",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le message",
      });
    },
  });

  const handleReply = (senderId: string, senderName: string) => {
    setSelectedUser({ id: senderId, name: senderName });
    setIsMessageDialogOpen(true);
  };

  const handleQuickReply = (senderId: string, senderName: string) => {
    setSelectedUser({ id: senderId, name: senderName });
    setIsComposingMessage(true);
  };

  const sendQuickReply = () => {
    if (!selectedUser || !quickReply.trim()) return;
    
    sendMessageMutation.mutate({
      content: quickReply,
      receiverId: selectedUser.id,
    });
  };

  const handleDelete = (messageId: string) => {
    deleteMessageMutation.mutate(messageId);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((n) => (
          <Card key={n} className="animate-pulse">
            <CardContent className="h-[100px]" />
          </Card>
        ))}
      </div>
    );
  }

  if (!messages?.length) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Vous n'avez pas encore reçu de messages.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Messages reçus</h2>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setIsMessageDialogOpen(true)}
          className="gap-1"
        >
          <Plus className="h-4 w-4" />
          Nouveau message
        </Button>
      </div>
      
      <ScrollArea className="h-[600px] pr-4 overflow-y-auto">
        <div className="space-y-4">
          {messages.map((message) => (
            <Card key={message.id} className="border-none shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-2 pt-4 bg-gradient-to-r from-slate-50 to-zinc-50">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-primary"></span>
                    De: {message.sender?.full_name || "Utilisateur"}
                  </CardTitle>
                  <span className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(message.created_at), {
                      addSuffix: true,
                      locale: fr,
                    })}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-sm mb-4 whitespace-pre-wrap">{message.content}</p>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickReply(message.sender_id, message.sender?.full_name || "Utilisateur")}
                    className="flex items-center gap-2"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Réponse rapide
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleReply(message.sender_id, message.sender?.full_name || "Utilisateur")}
                    className="flex items-center gap-2"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Répondre
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(message.id)}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Supprimer
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>

      {isComposingMessage && selectedUser && (
        <Card className="border-none shadow-md mt-4 bg-primary/5">
          <CardHeader className="pb-2 pt-4">
            <CardTitle className="text-sm">
              Réponse rapide à {selectedUser.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-4">
              <Textarea
                value={quickReply}
                onChange={(e) => setQuickReply(e.target.value)}
                placeholder="Écrivez votre réponse ici..."
                className="min-h-[100px]"
              />
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setIsComposingMessage(false)}
                >
                  Annuler
                </Button>
                <Button 
                  size="sm"
                  onClick={sendQuickReply}
                  disabled={!quickReply.trim()}
                >
                  Envoyer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedUser && (
        <MessageDialog
          open={isMessageDialogOpen}
          onOpenChange={setIsMessageDialogOpen}
          professionalId={selectedUser.id}
          professionalName={selectedUser.name}
        />
      )}
    </div>
  );
};
